/**
 * Assets Form Component
 *
 * Dynamic form for managing assets in financial profile.
 */

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  GlassButton,
  GlassIconButton,
  GlassTooltip,
  Grid,
  MagicInput,
  MagicSelect,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useMemo } from 'react';
import { ASSET_TYPE_OPTIONS, type Asset } from '@/lib/constants/financial-profile';

const ASSET_TYPE_I18N: Record<string, string> = {
  checking: 'assets.type.checking',
  savings: 'assets.type.savings',
  investment: 'assets.type.investment',
  retirement: 'assets.type.retirement',
  real_estate: 'assets.type.realEstate',
  vehicle: 'assets.type.vehicle',
  other: 'assets.type.other',
};

interface AssetsFormProps {
  assets: Asset[];
  onAdd: (asset: Omit<Asset, 'id'>) => void;
  onUpdate: (id: string, updates: Partial<Asset>) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
  maxAssets?: number;
}

export default function AssetsForm({
  assets,
  onAdd,
  onUpdate,
  onRemove,
  disabled = false,
  maxAssets = 10,
}: AssetsFormProps) {
  const { t } = useI18n();
  const translatedAssetOptions = useMemo(
    () => ASSET_TYPE_OPTIONS.map((opt) => ({ value: opt.value, label: t(ASSET_TYPE_I18N[opt.value] ?? opt.label) })),
    [t],
  );
  const handleAddAsset = () => {
    if (assets.length < maxAssets) {
      onAdd({
        assetType: 'checking',
        institution: '',
        approximateValue: 0,
      });
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {t('assets.description')}
      </Typography>

      {assets.length === 0 && (
        <Box
          sx={{
            p: 3,
            mb: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {t('assets.empty')}
          </Typography>
        </Box>
      )}

      {assets.map((asset, index) => (
        <Box
          key={asset.id}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1,
              mb: 2,
            }}
          >
            <Typography variant="subtitle2">{t('assets.assetNumber', { number: index + 1 })}</Typography>
            <GlassTooltip title={t('assets.removeAsset')}>
              <GlassIconButton size="small" onClick={() => onRemove(asset.id)} color="error" disabled={disabled}>
                <DeleteIcon fontSize="small" />
              </GlassIconButton>
            </GlassTooltip>
          </Box>

          <Grid container spacing={2}>
            {/* Asset Type */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicSelect
                fullWidth
                required
                disabled={disabled}
                label={t('assets.assetType')}
                value={asset.assetType}
                onChange={(e) => onUpdate(asset.id, { assetType: e.target.value as Asset['assetType'] })}
                size="small"
                options={translatedAssetOptions}
              />
            </Grid>

            {/* Institution */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                size="small"
                label={t('assets.institution')}
                value={asset.institution}
                onChange={(e) => onUpdate(asset.id, { institution: e.target.value })}
                disabled={disabled}
                required
                placeholder={t('assets.institutionPlaceholder')}
              />
            </Grid>

            {/* Approximate Value */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                size="small"
                label={t('assets.approximateValue')}
                type="number"
                value={asset.approximateValue}
                onChange={(e) => onUpdate(asset.id, { approximateValue: Number.parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                required
                slotProps={{
                  input: {
                    startAdornment: <span style={{ marginRight: 8 }}>$</span>,
                    inputProps: { min: 0, step: 100 },
                  },
                }}
              />
            </Grid>

            {/* Optional Description */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                size="small"
                label={t('assets.additionalNotes')}
                value={asset.description ?? ''}
                onChange={(e) => onUpdate(asset.id, { description: e.target.value })}
                disabled={disabled}
                placeholder={t('assets.notesPlaceholder')}
              />
            </Grid>
          </Grid>
        </Box>
      ))}

      {assets.length < maxAssets && (
        <GlassButton
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddAsset}
          disabled={disabled}
        >
          {t('assets.addAsset')}
        </GlassButton>
      )}

      {assets.length >= maxAssets && (
        <Typography variant="caption" color="textSecondary">
          {t('assets.maxAllowed', { max: maxAssets })}
        </Typography>
      )}
    </Box>
  );
}
